// Phase 6 — Track D T-D4. PaymentRow.

import type { PaymentTransaction } from '@dailydeck/shared/types';
import { ListRow } from '../../components/ui/ListRow.js';
import { formatDate } from '../../lib/dateFormat.js';

function formatAmount(amountCents: number, currency: string): string {
  const value = amountCents / 100;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Unknown currency code — fall back to a plain decimal.
    return `${currency} ${value.toFixed(2)}`;
  }
}

export interface PaymentRowProps {
  payment: PaymentTransaction;
  today: Date;
  /** Optional explicit row height for virtualisation alignment. */
  style?: React.CSSProperties;
}

export function PaymentRow({ payment, today, style }: PaymentRowProps): JSX.Element {
  const dateValue = payment.paid_on ?? payment.due_on ?? '';
  return (
    <ListRow
      data-payment-id={payment.id}
      data-testid="payment-row"
      style={style}
      className="grid grid-cols-[7rem_1fr_8rem] items-center gap-3"
    >
      <span className="text-caption text-text-muted tabular">
        {formatDate(dateValue, today)}
      </span>
      <div className="min-w-0">
        <p className="text-body-strong truncate">{payment.merchant}</p>
        {payment.description !== null && payment.description !== '' && (
          <p className="text-caption text-text-secondary truncate">{payment.description}</p>
        )}
      </div>
      <span className="text-body-strong tabular text-right">
        {formatAmount(payment.amount_cents, payment.currency)}
      </span>
    </ListRow>
  );
}
